import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import axios from "axios";
import config from "../../config.json";

// Import authentic cathedral photos
import church1 from "../../assets/img/church/church1.jpg";
import church2 from "../../assets/img/church/church2.jpg";
import church3 from "../../assets/img/church/church3.jpg";
import church4 from "../../assets/img/church/church4.jpg";
import church5 from "../../assets/img/church/church5.jpg";
import church6 from "../../assets/img/church/church6.jpg";
import church7 from "../../assets/img/church/church7.jpg";
import church8 from "../../assets/img/church/church8.jpg";
import church9 from "../../assets/img/church/church9.jpg";
import church10 from "../../assets/img/church/church10.jpg";
import church11 from "../../assets/img/church/church11.jpg";
import church12 from "../../assets/img/church/church12.jpg";
import church13 from "../../assets/img/church/church13.jpg";
import church14 from "../../assets/img/church/church14.jpg";
import church15 from "../../assets/img/church/church15.jpg";
import church16 from "../../assets/img/church/church16.jpg";
import churchMain from "../../assets/img/church.jpg";
//import slide1 from "../../assets/img/slide1.jpg";
//import slide2 from "../../assets/img/slide2.jpg";

export default function GalleryViewer() {
  const [showModal, setShowModal] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const [activeFilter, setActiveFilter] = useState("all");
  const [galleryImages, setGalleryImages] = useState([]);

  // Curated collections using authentic church photography
  const defaultAlbums = useMemo(() => [
    {
      id: 1,
      title: "Cathedral Edifice & Architecture",
      category: "architecture",
      date: "Romanesque Heritage",
      description: "The magnificent 42-meter bell towers, façade, and exterior of St. Mary's Cathedral.",
      images: [churchMain, church1, church2, church3, church4]
    },
    {
      id: 2,
      title: "Liturgical Celebrations & Holy Mass",
      category: "liturgy",
      date: "Sacred Feasts",
      description: "Moments of Eucharistic celebration, novenas, and solemn cathedral blessings.",
      images: [church5, church6, church7, church8, church9, church10]
    },
    {
      id: 3,
      title: "Parish Community & Fellowship",
      category: "community",
      date: "Faith in Action",
      description: "Our vibrant parishioners, youth ministry, and pious associations.",
      images: [church11, church12, church13, church14, church15, church16]
    }
  ], []);

  const fetchGalleryImages = useCallback(async () => {
    if (!config?.api?.invokeUrl) {
      return;
    }
    try {
      const fileName = "images/gallery/";
      const bucketName = "images.stmaryscathedral.in";
      const response = await axios.get(
        `${config.api.invokeUrl}/website/gallery?fileName=${fileName}&bucketName=${bucketName}`,
        {
          headers: {
            "x-api-key": config.api.key || ""
          }
        }
      );
      if (Array.isArray(response.data) && response.data.length > 0) {
        const formatted = response.data.map((item, idx) => ({
          id: idx + 1,
          title: item.projectName || `Cathedral Album ${idx + 1}`,
          category: "parish",
          date: item.projectDate || "Parish Archive",
          description: "Photographs from cathedral activities and sacred events.",
          images: item.fileName.map((f) => `${config.s3?.imageUrl || ""}${f}`)
        }));
        setGalleryImages(formatted);
      }
    } catch (error) {
      console.log("Using local curated cathedral albums.");
    }
  }, []);

  useEffect(() => {
    fetchGalleryImages();
  }, [fetchGalleryImages]);

  const activeAlbums = galleryImages.length > 0 ? galleryImages : defaultAlbums;

  // Flattened images for tab filtering
  const allImages = useMemo(() => {
    const list = [];
    activeAlbums.forEach((album) => {
      album.images.forEach((img, index) => {
        list.push({
          src: img,
          albumTitle: album.title,
          category: album.category,
          idx: index
        });
      });
    });
    return list;
  }, [activeAlbums]);

  const filteredImages = useMemo(() => {
    if (activeFilter === "all") return allImages;
    return allImages.filter((item) => item.category === activeFilter);
  }, [activeFilter, allImages]);

  const handleOpenModal = (imgItem) => {
    setSelectedImg(imgItem);
    setShowModal(true);
  };

  const handlePrevImg = () => {
    const currentIndex = filteredImages.findIndex((i) => i.src === selectedImg.src);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    setSelectedImg(filteredImages[prevIndex]);
  };

  const handleNextImg = () => {
    const currentIndex = filteredImages.findIndex((i) => i.src === selectedImg.src);
    const nextIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImg(filteredImages[nextIndex]);
  };

  return (
    <div className="gallery-page-wrapper" style={{ paddingTop: "140px", paddingBottom: "80px", background: "#fcfbf8", minHeight: "100vh" }}>
      <div className="container">
        {/* Page Header */}
        <div className="section-heading-wrapper text-center mb-5">
          <div className="eyebrow-badge">
            <i className="fas fa-camera"></i>
            <span>Visual Heritage & Memories</span>
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "var(--cathedral-navy)" }}>
            Cathedral Photo Gallery
          </h1>
          <div className="cross-ornament">
            <i className="fas fa-cross"></i>
          </div>
          <p style={{ maxWidth: "700px", margin: "0 auto", color: "var(--cathedral-text-secondary)" }}>
            Explore sacred moments, liturgical ceremonies, architectural highlights, and the joyful fellowship of St. Mary's Cathedral parish family.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="d-flex flex-wrap justify-content-center gap-2 mb-5">
          <button
            className={`btn ${activeFilter === "all" ? "btn-cathedral-primary" : "btn-cathedral-outline"}`}
            onClick={() => setActiveFilter("all")}
            style={{ fontSize: "0.85rem", padding: "8px 20px" }}
          >
            All Photos ({allImages.length})
          </button>
          <button
            className={`btn ${activeFilter === "architecture" ? "btn-cathedral-primary" : "btn-cathedral-outline"}`}
            onClick={() => setActiveFilter("architecture")}
            style={{ fontSize: "0.85rem", padding: "8px 20px" }}
          >
            <i className="fas fa-landmark me-1"></i> Architecture
          </button>
          <button
            className={`btn ${activeFilter === "liturgy" ? "btn-cathedral-primary" : "btn-cathedral-outline"}`}
            onClick={() => setActiveFilter("liturgy")}
            style={{ fontSize: "0.85rem", padding: "8px 20px" }}
          >
            <i className="fas fa-pray me-1"></i> Liturgy & Feasts
          </button>
          <button
            className={`btn ${activeFilter === "community" ? "btn-cathedral-primary" : "btn-cathedral-outline"}`}
            onClick={() => setActiveFilter("community")}
            style={{ fontSize: "0.85rem", padding: "8px 20px" }}
          >
            <i className="fas fa-users me-1"></i> Parish Community
          </button>
        </div>

        {/* Gallery Image Grid */}
        <div className="row g-4">
          {filteredImages.map((imgItem, idx) => (
            <div key={idx} className="col-12 col-sm-6 col-lg-4 col-xl-3">
              <div
                className="cathedral-card h-100"
                style={{
                  cursor: "pointer",
                  overflow: "hidden",
                  borderRadius: "14px",
                  boxShadow: "0 4px 16px rgba(27, 58, 107, 0.05)"
                }}
                onClick={() => handleOpenModal(imgItem)}
              >
                <div style={{ height: "230px", overflow: "hidden", position: "relative", background: "#f0ede6" }}>
                  <img
                    src={imgItem.src}
                    alt={imgItem.albumTitle}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.4s ease"
                    }}
                    onMouseOver={(e) => { e.currentTarget.style.transform = "scale(1.06)"; }}
                    onMouseOut={(e) => { e.currentTarget.style.transform = "scale(1.0)"; }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(to top, rgba(16, 37, 68, 0.6) 0%, transparent 50%)",
                      pointerEvents: "none"
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "12px",
                      color: "#ffffff",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      textShadow: "0 1px 3px rgba(0,0,0,0.6)"
                    }}
                  >
                    <i className="fas fa-search-plus me-1 text-secondary"></i> View
                  </span>
                </div>
                <div className="p-3">
                  <h6
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      color: "var(--cathedral-navy)",
                      fontSize: "0.95rem",
                      marginBottom: "4px"
                    }}
                  >
                    {imgItem.albumTitle}
                  </h6>
                  <small style={{ color: "#748396", fontSize: "0.78rem" }}>St. Mary's Cathedral Archive</small>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal Lightbox */}
        {selectedImg && (
          <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            centered
            size="lg"
            contentClassName="border-0 bg-transparent"
          >
            <Modal.Body className="p-0 position-relative text-center">
              <div
                style={{
                  background: "#ffffff",
                  padding: "16px",
                  borderRadius: "16px",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.3)"
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-3 px-2">
                  <h6
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      color: "var(--cathedral-navy)",
                      fontSize: "1.1rem",
                      marginBottom: 0
                    }}
                  >
                    {selectedImg.albumTitle}
                  </h6>
                  <button
                    onClick={() => setShowModal(false)}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.3rem",
                      color: "#6b7c91",
                      cursor: "pointer"
                    }}
                    aria-label="Close modal"
                  >
                    &#10005;
                  </button>
                </div>

                <div style={{ maxHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", borderRadius: "10px" }}>
                  <img
                    src={selectedImg.src}
                    alt={selectedImg.albumTitle}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "68vh",
                      objectFit: "contain",
                      borderRadius: "8px"
                    }}
                  />
                </div>

                {/* Modal navigation buttons */}
                <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handlePrevImg}
                    style={{ borderRadius: "20px", padding: "6px 16px" }}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Previous
                  </Button>
                  <span style={{ fontSize: "0.82rem", color: "#748396" }}>
                    St. Mary's Cathedral, Madurai
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleNextImg}
                    style={{ borderRadius: "20px", padding: "6px 16px" }}
                  >
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </Button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        )}
      </div>
    </div>
  );
}
